import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { lastDayOfMonth } from "date-fns";
import {
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
  Alert,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Icon } from "@iconify/react";

import { calculateTermination } from "../../utils/terminationUtils";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import { createFullDateString } from "../../utils/appUtils";
import Emoji from "../emoji/Emoji";

import {
  useUpdateMemberMutation,
  useSendTerminationMailMutation,
} from "../../features/members/membersApiSlice";
import {
  bezahltBisArray,
  getDateFromPayedTillString,
} from "../form/fields/inputFields";

const STEP_ICONS = {
  configure: "mdi:file-document-edit-outline",
  success: "mdi:check-circle-outline",
  mailResult: "mdi:email-check-outline",
};

const STEP_LABELS = {
  configure: "Konfiguration",
  success: "Gespeichert",
  mailResult: "E-Mail",
};

const STEPS = ["configure", "success", "mailResult"];

/**
 * TerminateMemberDialog – Multi-step workflow
 * Step 1 (configure): date picker, calculate/manual toggle, preview
 * Step 2 (success): confirmation + send mail option
 * Step 3 (mailResult): mail success/error with retry
 */
const TerminateMemberDialog = ({ member, handleClose }) => {
  const [updateMember, { isLoading: isSaving }] = useUpdateMemberMutation();
  const [sendTerminationMail, { isLoading: isMailSending }] =
    useSendTerminationMailMutation();

  const [step, setStep] = useState("configure");
  const [mode, setMode] = useState("CALCULATE");
  const [selectedDate, setSelectedDate] = useState(
    member?.terminationDate ? dayjs(member.terminationDate) : dayjs(new Date())
  );
  const [lastBookingManually, setLastBookingManually] = useState("");
  const [terminationData, setTerminationData] = useState(null);
  const [mailError, setMailError] = useState(null);

  // Already-terminated editing state
  const [editingTerminated, setEditingTerminated] = useState(false);
  const alreadyTerminated = member?.status === "terminated";

  const hasAllInputs = useMemo(() => {
    return Boolean(
      member?.contractDate &&
        Number.isFinite(+member?.contractDuration) &&
        Number.isFinite(+member?.contractExtension) &&
        Number.isFinite(+member?.noticePeriod)
    );
  }, [member]);

  const calcDisabled = useMemo(() => {
    if (!hasAllInputs) return true;
    return !(selectedDate && selectedDate.isValid());
  }, [hasAllInputs, selectedDate]);

  const contractEmail = member?.contractRef?.email;
  const contractOwner =
    member?.contractRef?.owner ||
    `${member?.contractRef?.firstName || ""} ${
      member?.contractRef?.lastName || ""
    }`.trim();

  const computeCalculated = () => {
    if (calcDisabled) return;
    const input = {
      contractDate: new Date(member.contractDate),
      contractDuration: Number(member.contractDuration),
      contractExtension: Number(member.contractExtension),
      noticePeriod: Number(member.noticePeriod),
      terminationDate: selectedDate.toDate(),
    };
    const lastMonth = calculateTermination(input);
    const endDate = lastDayOfMonth(getDateFromPayedTillString(lastMonth));
    setTerminationData({
      mode: "CALCULATE",
      ...input,
      lastMonth,
      contractEndDate: endDate,
    });
  };

  const computeManual = (mmYYYY) => {
    if (!mmYYYY) {
      setTerminationData(null);
      return;
    }
    const endDate = lastDayOfMonth(getDateFromPayedTillString(mmYYYY));
    setTerminationData({
      mode: "MANUAL",
      contractDate: new Date(member.contractDate),
      contractDuration: Number(member.contractDuration),
      contractExtension: Number(member.contractExtension),
      noticePeriod: Number(member.noticePeriod),
      terminationDate: selectedDate?.isValid()
        ? selectedDate.toDate()
        : new Date(),
      lastMonth: mmYYYY,
      contractEndDate: endDate,
    });
  };

  React.useEffect(() => {
    if (mode === "CALCULATE") computeCalculated();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedDate]);

  const onClose = () => handleClose?.();

  const onToggleMode = () => {
    setTerminationData(null);
    setMode((m) => (m === "CALCULATE" ? "MANUAL" : "CALCULATE"));
  };

  const onChangeManualMonth = (e) => {
    const v = e.target.value;
    setLastBookingManually(v);
    computeManual(v);
  };

  const saveDisabled = !terminationData;

  // Step 1 → Step 2: save termination (no dialog replacement)
  const handleSave = async () => {
    if (!terminationData) return;

    const payload = {
      id: member._id,
      status: "terminated",
      terminationDate: terminationData.terminationDate,
      terminationOption:
        terminationData.mode === "CALCULATE" ? "CALCULATE" : "MANUAL",
      terminationInformation: {
        lastMonth: terminationData.lastMonth,
        contractEndDate: terminationData.contractEndDate,
        lastUpdated: new Date(),
      },
    };

    try {
      await updateMember(payload).unwrap();
      setStep("success");
    } catch (err) {
      console.error("Termination save error:", err);
      setMailError(
        err?.data?.message || "Kündigung konnte nicht gespeichert werden."
      );
      setStep("mailResult");
    }
  };

  // Step 2 → Step 3: send confirmation mail
  const handleSendMail = async () => {
    setMailError(null);
    try {
      await sendTerminationMail({
        memberId: member._id,
        terminationData: {
          terminationDate: terminationData?.terminationDate,
          lastMonth: terminationData?.lastMonth,
          contractEndDate: terminationData?.contractEndDate,
        },
      }).unwrap();
      setStep("mailResult");
    } catch (err) {
      setMailError(
        err?.data?.message ||
          "Kündigungsbestätigung konnte nicht gesendet werden."
      );
      setStep("mailResult");
    }
  };

  // Resend from already-terminated view
  const handleResendMail = async () => {
    setMailError(null);
    const tInfo = member?.terminationInformation;
    try {
      await sendTerminationMail({
        memberId: member._id,
        terminationData: {
          terminationDate: member?.terminationDate,
          lastMonth: tInfo?.lastMonth,
          contractEndDate: tInfo?.contractEndDate,
        },
      }).unwrap();
      setStep("mailResult");
    } catch (err) {
      setMailError(
        err?.data?.message ||
          "Kündigungsbestätigung konnte nicht gesendet werden."
      );
      setStep("mailResult");
    }
  };

  // Already-terminated info view
  if (alreadyTerminated && !editingTerminated && step === "configure") {
    return (
      <Box sx={{ p: 3 }}>
        <StepIndicator currentStep="success" />

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2,
            bgcolor: "success.lighter",
            border: 1,
            borderColor: "success.light",
            borderRadius: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Icon
              icon="mdi:check-circle"
              width={28}
              color="var(--mui-palette-success-main, #2e7d32)"
            />
            <Typography variant="h6" color="success.dark">
              {member.firstName} {member.lastName} wurde bereits gekündigt
            </Typography>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 1 }}
          >
            Kündigungsdetails
          </Typography>

          <InfoBlock member={member} />

          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Chip
              icon={<Icon icon="mdi:calendar-clock" width={18} />}
              label={`Einzug bis: ${
                member?.terminationInformation?.lastMonth || "—"
              }`}
              color="warning"
              variant="outlined"
            />
            {member?.terminationInformation?.lastUpdated && (
              <Chip
                icon={<Icon icon="mdi:clock-outline" width={18} />}
                label={`Geändert: ${new Date(
                  member.terminationInformation.lastUpdated
                ).toLocaleDateString("de-DE")}`}
                variant="outlined"
                size="small"
              />
            )}
          </Stack>
        </Paper>

        <Stack direction="row" spacing={1.5}>
          <Button
            onClick={() => setEditingTerminated(true)}
            variant="contained"
            color="primary"
            startIcon={<Icon icon="mdi:pencil" />}
          >
            Kündigung bearbeiten
          </Button>
          {contractEmail && (
            <Button
              onClick={handleResendMail}
              variant="outlined"
              color="secondary"
              disabled={isMailSending}
              startIcon={<Icon icon="mdi:email-fast-outline" />}
            >
              Bestätigung erneut senden
            </Button>
          )}
          {!contractEmail && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ alignSelf: "center" }}
            >
              Keine E-Mail-Adresse hinterlegt
            </Typography>
          )}
        </Stack>

        {/* Show mail result inline if resend was triggered */}
        {step === "mailResult" && (
          <Box sx={{ mt: 2 }}>
            <MailResultView
              mailError={mailError}
              onRetry={handleResendMail}
              isRetrying={isMailSending}
            />
          </Box>
        )}

        <DialogActions sx={{ px: 0, pt: 2 }}>
          <Button onClick={onClose}>Schließen</Button>
        </DialogActions>
      </Box>
    );
  }

  // Mail result after resend from already-terminated view
  if (alreadyTerminated && !editingTerminated && step === "mailResult") {
    return (
      <Box sx={{ p: 3 }}>
        <StepIndicator currentStep="mailResult" />

        <MailResultView
          mailError={mailError}
          onRetry={handleResendMail}
          isRetrying={isMailSending}
        />

        <DialogActions sx={{ px: 0, pt: 2 }}>
          <Button onClick={onClose} variant="contained">
            Schließen
          </Button>
        </DialogActions>
      </Box>
    );
  }

  // ── Step 3: Mail Result ──
  if (step === "mailResult") {
    return (
      <Box sx={{ p: 3 }}>
        <StepIndicator currentStep="mailResult" />

        {/* If this was a save error rather than a mail error */}
        {!terminationData?.lastMonth && mailError ? (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 2,
              bgcolor: "error.lighter",
              border: 1,
              borderColor: "error.light",
              borderRadius: 2,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Icon icon="mdi:alert-circle" width={28} color="#d32f2f" />
              <Box>
                <Typography variant="h6" color="error.dark">
                  Fehler beim Speichern
                </Typography>
                <Typography variant="body2" color="error.dark" sx={{ mt: 0.5 }}>
                  {mailError}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ) : (
          <MailResultView
            mailError={mailError}
            onRetry={handleSendMail}
            isRetrying={isMailSending}
          />
        )}

        <DialogActions sx={{ px: 0, pt: 2 }}>
          <Button onClick={onClose} variant="contained">
            Schließen
          </Button>
        </DialogActions>
      </Box>
    );
  }

  // ── Step 2: Success ──
  if (step === "success") {
    return (
      <Box sx={{ p: 3 }}>
        <StepIndicator currentStep="success" />

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2,
            bgcolor: "success.lighter",
            border: 1,
            borderColor: "success.light",
            borderRadius: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Icon
              icon="mdi:check-circle"
              width={32}
              color="var(--mui-palette-success-main, #2e7d32)"
            />
            <Box>
              <Typography variant="h6" color="success.dark">
                Kündigung gespeichert
              </Typography>
              <Typography
                variant="body2"
                color="success.dark"
                sx={{ mt: 0.5 }}
              >
                {member.firstName} {member.lastName} wurde erfolgreich
                gekündigt.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {terminationData && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Zusammenfassung
            </Typography>
            <Stack spacing={0.5}>
              <SummaryRow
                label="Kündigungsdatum"
                value={createFullDateString(
                  dayjs(terminationData.terminationDate).toDate()
                )}
              />
              <SummaryRow
                label="Letzter Beitragsmonat"
                value={terminationData.lastMonth}
                highlight
              />
              <SummaryRow
                label="Vertragsende"
                value={new Date(
                  terminationData.contractEndDate
                ).toLocaleDateString("de-DE")}
              />
            </Stack>
          </Paper>
        )}

        {/* Mail option */}
        <Paper variant="outlined" sx={{ p: 2, mb: 1, borderRadius: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <Icon icon="mdi:email-outline" width={22} />
            <Typography variant="subtitle1" fontWeight={600}>
              Kündigungsbestätigung senden
            </Typography>
          </Stack>

          {contractEmail ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Eine Bestätigung wird an{" "}
                <strong>{contractEmail}</strong>
                {contractOwner ? ` (${contractOwner})` : ""} gesendet.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendMail}
                disabled={isMailSending}
                startIcon={<Icon icon="mdi:email-fast-outline" />}
              >
                {isMailSending
                  ? "Wird gesendet…"
                  : "Bestätigung per E-Mail senden"}
              </Button>
            </>
          ) : (
            <Alert severity="info" variant="outlined">
              Keine E-Mail-Adresse hinterlegt. Die Bestätigung kann nicht
              automatisch versendet werden.
            </Alert>
          )}
        </Paper>

        <DialogActions sx={{ px: 0, pt: 2 }}>
          <Button onClick={onClose}>Schließen</Button>
        </DialogActions>
      </Box>
    );
  }

  // ── Step 1: Configure ──
  return (
    <Box sx={{ p: 3 }}>
      {(isSaving || isMailSending) && <LoadingScreen />}

      <StepIndicator currentStep="configure" />

      <Typography variant="h5" sx={{ mb: 0.5 }}>
        {member.firstName} {member.lastName} kündigen
      </Typography>
      {alreadyTerminated && (
        <Chip
          label="Bereits gekündigt – Daten werden aktualisiert"
          color="warning"
          size="small"
          sx={{ mb: 1 }}
        />
      )}

      <DialogContent sx={{ px: 0, pt: 1 }}>
        {member?.terminationInformation && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            Zuletzt geändert:{" "}
            {member.terminationInformation.lastUpdated
              ? new Date(
                  member.terminationInformation.lastUpdated
                ).toLocaleDateString("de-DE")
              : "—"}
          </Typography>
        )}

        {!hasAllInputs ? (
          <MissingDataHint member={member} />
        ) : (
          <>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Kündigungsdatum wählen
            </Typography>

            <FormControl>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="de"
              >
                <DatePicker
                  label="Kündigungsdatum"
                  format="DD.MM.YYYY"
                  value={selectedDate}
                  onChange={(val) => setSelectedDate(val)}
                />
              </LocalizationProvider>
              {!selectedDate?.isValid?.() && (
                <FormHelperText error>
                  Bitte ein gültiges Datum wählen.
                </FormHelperText>
              )}
            </FormControl>

            {/* Mode toggle */}
            <Grid
              component="label"
              container
              alignItems="center"
              spacing={1}
              sx={{ my: 2 }}
            >
              <Grid item>
                <Typography variant="subtitle2">MANUELL</Typography>
              </Grid>
              <Grid item>
                <Switch
                  checked={mode === "CALCULATE"}
                  onChange={onToggleMode}
                  name="calculate-termination-option-switch"
                />
              </Grid>
              <Grid item>
                <Typography variant="subtitle2">BERECHNEN</Typography>
              </Grid>
            </Grid>

            {mode === "CALCULATE" ? (
              <>
                <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
                  Das Ende der Beitragspflicht wird anhand von Vertragsbeginn,
                  Mindestlaufzeit, Verlängerungszeitraum und Kündigungsfrist
                  automatisch ermittelt.
                </Alert>
                {terminationData && terminationData.mode === "CALCULATE" && (
                  <TerminationDataView terminationData={terminationData} />
                )}
              </>
            ) : (
              <TerminateManuallyView
                lastBookingManually={lastBookingManually}
                onChangeManualMonth={onChangeManualMonth}
              />
            )}

            {/* Preview summary when data is available */}
            {terminationData && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mt: 2,
                  bgcolor: "action.hover",
                  borderRadius: 2,
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Icon icon="mdi:information-outline" width={20} />
                  <Typography variant="subtitle2">
                    Vorschau Kündigungsdaten
                  </Typography>
                </Stack>
                <Stack spacing={0.5}>
                  <SummaryRow
                    label="Kündigungsdatum"
                    value={createFullDateString(
                      dayjs(terminationData.terminationDate).toDate()
                    )}
                  />
                  <SummaryRow
                    label="Letzter Beitragsmonat"
                    value={terminationData.lastMonth}
                    highlight
                  />
                  <SummaryRow
                    label="Vertragsende"
                    value={new Date(
                      terminationData.contractEndDate
                    ).toLocaleDateString("de-DE")}
                  />
                </Stack>
              </Paper>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 0, pt: 1 }}>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          onClick={handleSave}
          disabled={saveDisabled || isSaving}
          color="primary"
          variant="contained"
          startIcon={<Icon icon="mdi:content-save-check-outline" />}
        >
          Kündigung speichern
        </Button>
      </DialogActions>
    </Box>
  );
};

export default TerminateMemberDialog;

/* ────────── Sub-Components ────────── */

function StepIndicator({ currentStep }) {
  const currentIdx = STEPS.indexOf(currentStep);
  return (
    <Stack
      direction="row"
      spacing={0}
      sx={{ mb: 2.5 }}
      justifyContent="center"
    >
      {STEPS.map((s, idx) => {
        const isDone = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <React.Fragment key={s}>
            {idx > 0 && (
              <Box
                sx={{
                  width: 40,
                  height: 2,
                  alignSelf: "center",
                  bgcolor: isDone || isCurrent ? "primary.main" : "divider",
                  mx: 0.5,
                  borderRadius: 1,
                }}
              />
            )}
            <Stack alignItems="center" spacing={0.5} sx={{ minWidth: 80 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isDone
                    ? "success.main"
                    : isCurrent
                    ? "primary.main"
                    : "action.disabledBackground",
                  color: isDone || isCurrent ? "#fff" : "text.disabled",
                  transition: "all 0.3s ease",
                }}
              >
                <Icon
                  icon={isDone ? "mdi:check" : STEP_ICONS[s]}
                  width={20}
                />
              </Box>
              <Typography
                variant="caption"
                color={isCurrent ? "primary.main" : "text.secondary"}
                fontWeight={isCurrent ? 700 : 400}
              >
                {STEP_LABELS[s]}
              </Typography>
            </Stack>
          </React.Fragment>
        );
      })}
    </Stack>
  );
}

function SummaryRow({ label, value, highlight }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={highlight ? 700 : 500}
        color={highlight ? "warning.dark" : "text.primary"}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function MailResultView({ mailError, onRetry, isRetrying }) {
  if (mailError) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          bgcolor: "error.lighter",
          border: 1,
          borderColor: "error.light",
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Icon icon="mdi:alert-circle" width={28} color="#d32f2f" />
          <Box>
            <Typography variant="h6" color="error.dark">
              E-Mail konnte nicht gesendet werden
            </Typography>
            <Typography variant="body2" color="error.dark" sx={{ mt: 0.5 }}>
              {mailError}
            </Typography>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={onRetry}
              disabled={isRetrying}
              startIcon={<Icon icon="mdi:refresh" />}
              sx={{ mt: 1.5 }}
            >
              {isRetrying ? "Wird erneut gesendet…" : "Erneut versuchen"}
            </Button>
          </Box>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2,
        bgcolor: "success.lighter",
        border: 1,
        borderColor: "success.light",
        borderRadius: 2,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Icon
          icon="mdi:email-check"
          width={28}
          color="var(--mui-palette-success-main, #2e7d32)"
        />
        <Box>
          <Typography variant="h6" color="success.dark">
            E-Mail erfolgreich gesendet
          </Typography>
          <Typography variant="body2" color="success.dark" sx={{ mt: 0.5 }}>
            Die Kündigungsbestätigung wurde versendet.
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

function TerminateManuallyView({ lastBookingManually, onChangeManualMonth }) {
  return (
    <div id="terminate-option-manually" style={{ marginTop: 8 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        Vertragsende manuell festlegen
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        Monat der <b>letzten Abbuchung</b> auswählen:
      </Typography>
      <Select
        labelId="bookMonth-select"
        id="bookMonth-select"
        variant="outlined"
        error={!lastBookingManually}
        value={lastBookingManually || ""}
        onChange={onChangeManualMonth}
        sx={{ minWidth: 180, mt: 1 }}
      >
        {bezahltBisArray.map((item) => (
          <MenuItem key={item.id} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
      <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
        Bis einschließlich dieses Monats wird der Mitgliedsbeitrag abgebucht.
      </Typography>
    </div>
  );
}

function TerminationDataView({ terminationData }) {
  return (
    <div id="terminate-option-calculated" style={{ marginTop: 12 }}>
      <Typography variant="body2">
        Vertragsdatum:&nbsp;
        {createFullDateString(dayjs(terminationData.contractDate).toDate())}
      </Typography>
      <Typography variant="body2">
        Vertragslaufzeit: {terminationData.contractDuration} Monate
      </Typography>
      <Typography variant="body2">
        Verlängerung: {terminationData.contractExtension} Monate
      </Typography>
      <Typography variant="body2">
        Kündigungsfrist: {terminationData.noticePeriod} Monate
      </Typography>
      <Typography variant="body2">
        Gekündigt am:&nbsp;
        {createFullDateString(dayjs(terminationData.terminationDate).toDate())}
      </Typography>
    </div>
  );
}

function MissingDataHint({ member }) {
  return (
    <div id="missing-data-hint" style={{ marginTop: 16 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Um die Kündigungsdaten korrekt zu berechnen, werden folgende Daten
        benötigt:
      </Typography>
      <Typography variant="body1" component="div">
        <ul style={{ marginTop: 4, marginBottom: 0 }}>
          <li>
            Vertragsdatum&nbsp;
            {member.contractDate ? (
              <Emoji symbol="✅" />
            ) : (
              <Emoji symbol="❌" />
            )}
          </li>
          <li>
            Vertragslaufzeit&nbsp;
            {member.contractDuration ? (
              <Emoji symbol="✅" />
            ) : (
              <Emoji symbol="❌" />
            )}
          </li>
          <li>
            Vertragsverlängerung&nbsp;
            {member.contractExtension ? (
              <Emoji symbol="✅" />
            ) : (
              <Emoji symbol="❌" />
            )}
          </li>
          <li>
            Kündigungsfrist&nbsp;
            {member.noticePeriod ? (
              <Emoji symbol="✅" />
            ) : (
              <Emoji symbol="❌" />
            )}
          </li>
        </ul>
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        <b>Fehlende Daten</b> sind durch <Emoji symbol="❌" /> gekennzeichnet.
        Bitte ergänzen Sie diese Daten unter <b>„Mitglied bearbeiten"</b>.
      </Typography>
    </div>
  );
}

function InfoBlock({ member }) {
  return (
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      <SummaryRow
        label="Vertragsdatum"
        value={new Date(member?.contractDate).toLocaleDateString("de-DE")}
      />
      <SummaryRow
        label="Vertragslaufzeit"
        value={`${member.contractDuration} Monat(e)`}
      />
      <SummaryRow
        label="Vertragsverlängerung"
        value={`${member.contractExtension} Monat(e)`}
      />
      <SummaryRow
        label="Kündigungsfrist"
        value={`${member.noticePeriod} Monat(e)`}
      />
      <SummaryRow
        label="Kündigungsdatum"
        value={
          member?.terminationDate
            ? new Date(member.terminationDate).toLocaleDateString("de-DE")
            : "—"
        }
      />
    </Stack>
  );
}
